import { Scope, Severity } from '@sentry/types';
import { HttpError } from '@wix/http-client';

import { IInitialChatData } from '../../server/service/InitialChatData';

type LogParams = {
  error: Error;
  message: string;
  callParams?: Record<string, any>;
};

type LogWidgetMessageParams = {
  payload: Record<string, any>;
  widgetEventType: string;
};

enum Tags {
  SOURCE = 'source',
  CONVERSATION_ID = 'conversation_id',
}

export enum EventSource {
  DUPLEXER = 'duplexer',
  EMITTER = 'emitter',
}

class Logger {
  error({ error, message, callParams = {} }: LogParams): void {
    try {
      window.Sentry.withScope((scope: Scope) => {
        const localError = new Error(message);
        scope.setExtra('originalError', error.message);
        scope.setExtra('originalErrorStack', error.stack);
        scope.setExtra('callParams', JSON.stringify(callParams));
        if (error instanceof HttpError) {
          const { status, statusText, data, headers } = error.response ?? {};
          scope.setExtra('httpErrorStatus', status);
          scope.setExtra('httpErrorStatusText', statusText);
          scope.setExtra('httpErrorData', data);
          scope.setExtra('X-Wix-Request-Id', headers['x-wix-request-id']);
        }
        window.Sentry.captureException(localError);
      });
    } catch (e) {}
  }

  info(message: string, data = {}): void {
    window.Sentry.withScope((scope: Scope) => {
      scope.setExtra('data', JSON.stringify(data));
      window.Sentry.captureMessage(message, Severity.Info);
    });
  }

  logChatbotStateChanged(message: string, source: EventSource, data: IInitialChatData): void {
    window.Sentry.withScope((scope: Scope) => {
      scope.setTag(Tags.SOURCE, source);
      scope.setTag(Tags.CONVERSATION_ID, data.conversationId);
      scope.setExtra('data', JSON.stringify(data));
      window.Sentry.captureMessage(message, Severity.Info);
    });
  }

  logWidgetMessage({ widgetEventType, payload }: LogWidgetMessageParams): void {
    window.Sentry.withScope((scope: Scope) => {
      scope.setExtra('eventParams', JSON.stringify(payload));
      window.Sentry.captureMessage(widgetEventType, Severity.Info);
    });
  }

  debug(debug?: unknown) {
    if (Array.isArray(debug) && debug.length > 0) {
      console.log.apply(null, debug);
    }
  }
}

export const logger = new Logger();
